import React from 'react';

interface State {
  error: Error | null;
  componentStack: string;
}

class AppErrorBoundary extends React.Component<React.PropsWithChildren<unknown>, State> {
  public constructor(props: React.PropsWithChildren<unknown>) {
    super(props);
    this.state = {
      error: null,
      componentStack: '',
    };
  }

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ componentStack: errorInfo.componentStack });
    // This is intentionally visible in production to diagnose otherwise blank pages.
    // eslint-disable-next-line no-console
    console.error('[TrackIn render error]', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      path: window.location.href,
    });
  }

  public render() {
    const { error, componentStack } = this.state;
    const { children } = this.props;

    if (!error) return children;

    return (
      <main className="min-h-screen bg-gray-50 p-8 text-gray-800">
        <section className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow">
          <h1 className="mb-3 text-2xl font-semibold">This page could not be displayed</h1>
          <p className="mb-4">
            The error has been written to the browser console under
            {' '}
            <strong>TrackIn render error</strong>
            .
          </p>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded bg-gray-100 p-4 text-sm">
            {error.message}
            {componentStack}
          </pre>
          <button
            className="mt-4 rounded bg-primary px-4 py-2 text-white"
            onClick={() => window.location.reload()}
            type="button"
          >
            Reload page
          </button>
        </section>
      </main>
    );
  }
}

export default AppErrorBoundary;
